"use client";

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronsUpDown } from "lucide-react";
import type { CatalogModel } from "@/lib/llm-models/catalog";
import { effortLabel } from "./format";

function DefaultBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
      model default
    </span>
  );
}

type Props = {
  id: string;
  testId?: string;
  value: string | null;
  model: CatalogModel;
  disabled: boolean;
  onChange: (effort: string) => void;
};

export default function EffortPicker({ id, testId, value, model, disabled, onChange }: Props) {
  return (
    <div data-testid={testId}>
      <Listbox value={value ?? ""} onChange={onChange} disabled={disabled}>
        <ListboxButton
          id={id}
          className="relative flex h-11 w-full items-center gap-2 rounded-lg border border-slate-300 bg-white pl-3 pr-10 text-left text-sm font-medium text-slate-900 shadow-sm transition hover:border-slate-400 focus:outline-none data-[focus]:border-indigo-500 data-[focus]:ring-2 data-[focus]:ring-indigo-500/20 data-[open]:border-indigo-500 data-[open]:ring-2 data-[open]:ring-indigo-500/20 data-[disabled]:cursor-not-allowed data-[disabled]:bg-slate-50 data-[disabled]:text-slate-500 data-[disabled]:hover:border-slate-300"
        >
          <span className="truncate">{value ? effortLabel(value) : "Choose an effort"}</span>
          {value && value === model.defaultEffort ? <DefaultBadge /> : null}
          <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          transition
          className="z-50 w-[var(--button-width)] min-w-[12rem] rounded-xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-900/10 [--anchor-gap:6px] focus:outline-none transition duration-100 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
        >
          {model.efforts.map((effort) => (
            <ListboxOption
              key={effort}
              value={effort}
              className="group flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-800 data-[focus]:bg-indigo-50"
            >
              <Check className="h-4 w-4 shrink-0 text-indigo-600 invisible group-data-[selected]:visible" aria-hidden="true" />
              <span className="font-medium group-data-[selected]:text-indigo-700">{effortLabel(effort)}</span>
              {effort === model.defaultEffort ? <DefaultBadge /> : null}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}
